import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pagination } from '../../components/ui/pagination';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { Concession } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { CONCESSION_STATUS_COLORS, RISK_LEVEL_COLORS, DEVIATION_TYPE_LABELS } from './constants';

interface ConcessionsTableProps {
  concessions: Concession[];
  searchTerm: string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  isCustomer: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    goToPage: (page: number) => void;
    reset: () => void;
  };
  onSearchChange: (value: string) => void;
  onSort: (field: string) => void;
  onViewConcession: (id: number) => void;
}

export function ConcessionsTable({
  concessions,
  searchTerm,
  sortField,
  sortDirection,
  isCustomer,
  pagination,
  onSearchChange,
  onSort,
  onViewConcession,
}: ConcessionsTableProps) {
  const { formatDate } = useFormatters();

  const getStatusBadge = (status: string) => {
    const config = CONCESSION_STATUS_COLORS[status];
    const label = config?.label || status;
    const className = config?.className || 'bg-transparent !text-black border-gray-300';
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const getRiskBadge = (riskLevel: string) => {
    const config = RISK_LEVEL_COLORS[riskLevel];
    const label = config?.label || riskLevel;
    const className = config?.className || 'bg-transparent !text-black border-gray-300';
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Concession Requests ({pagination.totalCount})</CardTitle>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search concessions..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {concessions.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {searchTerm ? 'No concessions found matching your search' : 'No concessions found'}
          </p>
        ) : (
          <>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              itemsPerPage={pagination.pageSize}
              onPageChange={pagination.goToPage}
              itemLabel="concessions"
            />

            <div className="overflow-x-auto mt-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th
                      className="text-left py-3 px-4 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => onSort('concession_number')}
                    >
                      <div className="flex items-center">
                        Concession #
                        {getSortIcon('concession_number')}
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Title</th>
                    <th
                      className="text-left py-3 px-4 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => onSort('part_number')}
                    >
                      <div className="flex items-center">
                        Part Number
                        {getSortIcon('part_number')}
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => onSort(isCustomer ? 'supplier_name' : 'customer_name')}
                    >
                      <div className="flex items-center">
                        {isCustomer ? 'Supplier' : 'Customer'}
                        {getSortIcon(isCustomer ? 'supplier_name' : 'customer_name')}
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Type</th>
                    <th
                      className="text-left py-3 px-4 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => onSort('risk_level')}
                    >
                      <div className="flex items-center">
                        Risk
                        {getSortIcon('risk_level')}
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => onSort('requested_date')}
                    >
                      <div className="flex items-center">
                        Requested
                        {getSortIcon('requested_date')}
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-sm cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => onSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {concessions.map((concession) => (
                    <tr
                      key={concession.id}
                      className="border-b hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-3 px-4 font-mono text-sm">{concession.concession_number}</td>
                      <td className="py-3 px-4 max-w-[200px] truncate" title={concession.title}>
                        {concession.title}
                      </td>
                      <td className="py-3 px-4 font-mono">{concession.part_number}</td>
                      <td className="py-3 px-4">
                        {isCustomer ? concession.supplier_name : concession.customer_name}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {DEVIATION_TYPE_LABELS[concession.deviation_type] || concession.deviation_type}
                      </td>
                      <td className="py-3 px-4">{getRiskBadge(concession.risk_level)}</td>
                      <td className="py-3 px-4">{formatDate(concession.requested_date)}</td>
                      <td className="py-3 px-4">{getStatusBadge(concession.status)}</td>
                      <td className="py-3 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewConcession(concession.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              itemsPerPage={pagination.pageSize}
              onPageChange={pagination.goToPage}
              itemLabel="concessions"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
